import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Button, CircularProgress, Divider, InputAdornment, Stack, TextField, Typography } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { apiDelete, apiDownloadFile, apiFile, apiGet, apiPut } from '../../../../generic/Api_Functions'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import MosSmartDialog from '../MosSmartDialog'
import createDialogTitle from '../../../../generic/utilities/dialogUtil'
// De la pieza HOJA, sin imports: preguntar si es previsualizable NO puede cargar el visor (R4/S57).
import { isExtensionViewable } from '../../../../configuration/document/documentExtensions'
import {
  buildFabricationDocumentsUrl,
  buildMosDocumentContextQuery,
  buildMosDocumentCreateUrl,
  buildMosDocumentDeleteUrl,
  buildMosDocumentDownloadUrl,
  buildMosDocumentUpdateUrl
} from './fabricationDocumentsApi'
import { SHARED_QUALITY_CATALOGUE_TYPES } from './fabricationDocumentSources'
import { filterGroupsByDocumentName } from './fabricationDocumentSearch'
import FabricationDocumentGroupList from './FabricationDocumentGroupList'
import FabricationDocumentViewer from './FabricationDocumentViewer'
import FabricationDocumentAddForm from './FabricationDocumentAddForm'
import documentOfEntry from './documentOfEntry'

// Historia 148 — el CUERPO de la documentación de UNA fabricación: lista, búsqueda, visor en dos
// paneles, acciones y alta superpuesta. La historia 160 lo extrae del diálogo de #148 (riesgo R-D1)
// para que el detalle del job lo monte como pestaña sin diálogo exterior; el diálogo de #148 lo
// envuelve y conserva su comportamiento.
//
// ⚠️ REPARTO DE RESPONSABILIDAD (D1 + §«Lo que no cruza»): este componente **no decide visibilidad
// de grupos** y **no deriva procedencias**. Sun devuelve ya filtrado por permisos y ya ordenado;
// aquí se PINTA lo que llega. Si en este fichero aparece un «ocultar grupo por falta de permiso» o
// un «calcular las materias primas del job», contradice el contrato: RN4-RN7 y RN16 viven en Sun.
//
// ⚠️ `Document.jsx` NO se toca: es contrato con muchas pantallas de configuración. Este modal
// reutiliza las piezas HOJA de MIC —`documentExtensions` para decidir si algo es previsualizable
// y `SmartDocumentViewer`, EL MISMO visor que abre MIC desde su tabla— nunca el contenedor.
//
// ⚠️ DOS PANELES, no dos ventanas. A la izquierda la lista; a la derecha el documento seleccionado.
// Un segundo modal encima del modal obliga al operario a cerrarlo para volver a la lista y, con
// guantes, a acertar dos veces. Con los dos paneles, cambiar de documento es UN toque.
//
// ⚠️ EL PANEL NECESITA ALTURA PROPIA (`height`). `SmartDocumentViewer` y sus visores 2D/3D pintan
// con `height: '100%'`: montados en un contenedor de altura automática resuelven a CERO píxeles y el
// fichero se descarga pero no se ve.
//
// ⚠️ S14/RN33 — LA LISTA NO SE REFRESCA SOLA. El panel carga SOLO cuando cambia el par que recibe o
// su `requestToken`; nunca por un re-render de quien lo monta. El diálogo de #148 le pasa los ids
// CONGELADOS al abrir y un token que crece en cada apertura; el detalle del job (#160) le pasa el par
// del job seleccionado, que solo cambia al cambiar de job (RN36).

const EMPTY_DOCUMENTATION = { fabricationOrderId: '', jobReferenceId: '', groups: [] }
// Ningún documento abierto. `entry` vacío es el discriminador; nada de `null` en el estado.
// ⚠️ `entry` es LO QUE SE ESTA MOSTRANDO (lo mueve tambien el carrusel con sus flechas), mientras
// que `requestedUUID` es LO QUE EL OPERARIO PIDIO EN LA LISTA y solo cambia al pulsar una fila.
// Tienen que ser dos cosas distintas: si al carrusel se le pasa como «documento inicial» el mismo
// estado que el actualiza al mostrar algo, se realimenta y el modal se queda clavado en el primer
// documento del registro, elijas el que elijas.
const NO_OPENED_DOCUMENT = { entry: '', source: {}, permissions: {}, previewable: false, requestedUUID: '', pickToken: 0 }
// Ninguna acción en curso. `entry` vacío es el discriminador, igual que arriba.
const NO_ACTION = { entry: '', source: {} }
// 85vh de papel menos el titulo del dialogo y su barra de acciones.
export const DIALOG_PANES_HEIGHT = 'calc(85vh - 150px)'

const isLoadable = (token, fabricationOrderId, jobReferenceId) =>
  token !== 0 && fabricationOrderId !== '' && jobReferenceId !== ''

/**
 * @param {Object} props
 * @param {string|number} props.fabricationOrderId
 * @param {string|number} props.jobReferenceId `jobReference.id`, nunca el id del nodo de workflow
 * @param {number} props.requestToken 0 = no cargar todavía; cada valor nuevo recarga el mismo par
 * @param {string|number} props.height altura acotada de los dos paneles
 */
const FabricationDocumentsPanel = ({ fabricationOrderId = '', jobReferenceId = '', requestToken = 1, height = DIALOG_PANES_HEIGHT }) => {
  const { t_ } = useSmartTranslation()
  const [documentation, setDocumentation] = useState(EMPTY_DOCUMENTATION)
  // Nace cargando si ya tiene qué cargar: así el primer pintado no dice «sin documentación» durante
  // el instante que tarda en arrancar el efecto de carga.
  const [loading, setLoading] = useState(() => isLoadable(requestToken, fabricationOrderId, jobReferenceId))
  // El literal estable que devuelve Sun (`FabricationOrderNotFound`, …). Se usa como CLAVE de
  // traducción (S48): el operario nunca lee el literal crudo.
  const [errorLiteral, setErrorLiteral] = useState('')
  const [hasFailed, setHasFailed] = useState(false)
  const request = useMemo(
    () => ({ token: requestToken, fabricationOrderId, jobReferenceId }),
    [requestToken, fabricationOrderId, jobReferenceId]
  )
  const [retryToken, setRetryToken] = useState(0)
  // D10: la búsqueda reduce LOCALMENTE lo ya recibido. Nunca consulta al servidor, así que no puede
  // sacar a la luz un documento de un grupo que Sun no devolvió por falta de permiso (S66).
  const [searchText, setSearchText] = useState('')
  // El documento abierto vive DENTRO del mismo modal: el operario no sale de MOS (RN24/S57).
  const [openedDocument, setOpenedDocument] = useState(NO_OPENED_DOCUMENT)
  // Clave i18n del aviso del visor (S58) y del aviso sobre la lista cuando ya no existe (S67).
  const [viewerErrorKey, setViewerErrorKey] = useState('')
  const [listNoticeKey, setListNoticeKey] = useState('')
  // ⚠️ El aviso de MIC por documento multi-enlazado se muestra TAL CUAL (S74/CA31/RN34): es un
  // texto preexistente de MIC, no un literal acuñado por esta historia, y no se traduce.
  const [listNoticeRaw, setListNoticeRaw] = useState('')
  const [addOpen, setAddOpen] = useState(false)
  const [editing, setEditing] = useState(NO_ACTION)
  const [editValues, setEditValues] = useState({ documentName: '', description: '' })
  const [deleting, setDeleting] = useState(NO_ACTION)

  // Cuenta de elecciones desde la lista; vive en una ref porque solo alimenta la `key` del visor.
  const openedDocumentPickTokenRef = useRef(0)

  // ⚠️ El efecto de carga depende SOLO de la petición (par + token), nunca de otras props de quien
  // lo monta. Lo detectó S14 de #148.
  useEffect(() => {
    if (!isLoadable(request.token, request.fabricationOrderId, request.jobReferenceId)) {
      return
    }
    let isCurrent = true
    setLoading(true)
    setErrorLiteral('')
    setHasFailed(false)
    // Cada apertura empieza con la lista entera; el texto solo sobrevive dentro de una sesión del
    // modal (S61: abrir y cerrar un documento no lo borra).
    setSearchText('')
    setOpenedDocument(NO_OPENED_DOCUMENT)
    setAddOpen(false)
    setEditing(NO_ACTION)
    setDeleting(NO_ACTION)
    apiGet(buildFabricationDocumentsUrl(request.fabricationOrderId, request.jobReferenceId))
      .then((data) => {
        if (!isCurrent) {
          return
        }
        setDocumentation({ ...EMPTY_DOCUMENTATION, ...data, groups: data?.groups || [] })
        setLoading(false)
      })
      .catch((error) => {
        if (!isCurrent) {
          return
        }
        // Un solo intento: el fallo ofrece reintentar, nunca entra en un ciclo automático (S56).
        setDocumentation(EMPTY_DOCUMENTATION)
        setErrorLiteral(error?.message || '')
        setHasFailed(true)
        setLoading(false)
      })
    return () => {
      isCurrent = false
    }
  }, [request, retryToken])

  const handleRetry = useCallback(() => setRetryToken((token) => token + 1), [])

  // El contexto MOS de la aparición PULSADA (D4/RN35): tipo y registro de ESA fila, más el par de la
  // fabricación congelado al abrir. Nunca «la procedencia del documento».
  const contextOf = useCallback((source) => ({
    fabricationOrderId: request.fabricationOrderId,
    jobReferenceId: request.jobReferenceId,
    sourceType: source.sourceType,
    sourceReferenceId: source.sourceReferenceId
  }), [request])

  const handleDownload = useCallback((entry, source) => {
    apiDownloadFile(
      buildMosDocumentDownloadUrl(entry.document?.uuid, contextOf(source)),
      entry.document?.fileName || ''
    )
  }, [contextOf])

  const openDocument = useCallback((entry, source, permissions) => {
    setViewerErrorKey('')
    setListNoticeKey('')
    openedDocumentPickTokenRef.current += 1
    // RN25/S59/S60: un formato que no se puede mostrar se dice explícitamente. La decisión se toma
    // con la pieza hoja de extensiones, SIN cargar el visor (R4/S57).
    // ⚠️ Y se toma UNA vez, con el documento ELEGIDO. Si se recalculara con lo que el carrusel va
    // mostrando, llegar con las flechas a un hermano no previsualizable cambiaría de rama y
    // desmontaría el carrusel: el operario se quedaría sin flechas para salir de ahí.
    setOpenedDocument({
      entry,
      source,
      permissions,
      previewable: isExtensionViewable(entry.document?.extension || ''),
      requestedUUID: entry.document?.uuid || '',
      // Cada eleccion desde la lista es un gesto nuevo, aunque sea sobre la MISMA fila: tras haberse
      // movido con las flechas, volver a pulsarla tiene que traer ese documento otra vez.
      pickToken: openedDocumentPickTokenRef.current
    })
  }, [])

  // Los documentos del MISMO REGISTRO que el abierto: son los que el carrusel ofrece en su tira de
  // miniaturas. Salen de lo que el endpoint agregador YA devolvio —nunca de una consulta propia—,
  // asi que la navegacion entre miniaturas no puede sacar a la luz nada que Sun filtrara (RN16).
  // ⚠️ `useMemo`, NO `useCallback` que se invoque al pintar. El carrusel centra el documento inicial
  // en un efecto que depende de la IDENTIDAD de este array; con un array nuevo en cada render —y el
  // sondeo de MOS repinta el modal cada pocos segundos— ese efecto se redispara y devuelve el
  // carrusel al documento que se pulso en la lista: las flechas dejan de funcionar.
  const documentsOfOpenedRecord = useMemo(() => {
    const group = documentation.groups.find((candidate) => candidate.sourceType === openedDocument.source.sourceType)
    if (group === undefined) {
      return []
    }
    return group.entries
      .filter((entry) => entry.sourceReferenceId === openedDocument.source.sourceReferenceId)
      .map(documentOfEntry)
  }, [documentation, openedDocument.source.sourceType, openedDocument.source.sourceReferenceId])

  // El indice vive DENTRO del carrusel (flechas y miniaturas), asi que es el quien avisa de que
  // documento se esta mostrando. Sin esto el operario ve un documento y tiene otro marcado en la
  // lista — y la marca es lo que le dice de que registro y de que fila viene lo que mira.
  const handleViewerDocumentChange = useCallback((document) => {
    if (!document?.uuid) {
      return
    }
    setOpenedDocument((previous) => {
      if (previous.entry === '' || previous.entry.document?.uuid === document.uuid) {
        return previous
      }
      const group = documentation.groups.find((candidate) => candidate.sourceType === previous.source.sourceType)
      const entry = group?.entries.find(
        (candidate) => candidate.sourceReferenceId === previous.source.sourceReferenceId &&
          candidate.document?.uuid === document.uuid
      )
      if (entry === undefined) {
        return previous
      }
      // `previewable` NO se recalcula: lo fijo la eleccion desde la lista. Ver el comentario de
      // `openDocument`.
      return { ...previous, entry }
    })
  }, [documentation])

  const backToList = useCallback(() => {
    setViewerErrorKey('')
    setOpenedDocument(NO_OPENED_DOCUMENT)
  }, [])

  // S67 — un documento borrado desde oficina mientras el modal estaba abierto. El 404 se distingue
  // de un fallo de red: se vuelve a la lista, se avisa y la lista se RECARGA, sin cerrar el modal ni
  // perder el contexto de MOS. Cualquier otro fallo deja el aviso accionable en el propio visor (S58).
  const handleViewerLoadError = useCallback((error) => {
    if (error?.status === 404) {
      setOpenedDocument(NO_OPENED_DOCUMENT)
      setListNoticeKey('This document is no longer available')
      setRetryToken((token) => token + 1)
      return
    }
    setViewerErrorKey('The document could not be opened')
  }, [])

  // El PANEL DERECHO. Nunca desaparece: o muestra el documento seleccionado, o dice que se
  // seleccione uno. Cerrar el documento (RN26/S61) solo vacía este panel — la lista de la izquierda
  // sigue ahí con su búsqueda y su posición intactas.
  const renderViewerPane = () => {
    if (openedDocument.entry === '') {
      return (
        <Stack alignItems='center' justifyContent='center' sx={{ height: '100%', minHeight: 240 }}>
          <Typography color='text.secondary'>{t_('Select a document to view it')}</Typography>
        </Stack>
      )
    }

    const { entry, source, permissions, previewable } = openedDocument
    const backButton = (
      <Button variant='outlined' onClick={backToList} sx={{ minHeight: 48, minWidth: 48 }}>
        {t_('Back to the list')}
      </Button>
    )

    // El carrusel ya rotula «<nombre del documento> - <nombre de fichero>» en su propia cabecera:
    // repetirlo encima solo roba altura al documento. Aqui queda unicamente la vuelta a la lista, y
    // de que registro es se sabe por la fila marcada en la columna izquierda.
    const header = (
      <Stack direction='row' justifyContent='flex-end' alignItems='center' sx={{ flex: '0 0 auto' }}>
        {backButton}
      </Stack>
    )

    if (!previewable) {
      return (
        <Stack spacing={2} sx={{ height: '100%', minHeight: 0 }}>
          {header}
          <Stack alignItems='center' justifyContent='center' spacing={2} sx={{ flexGrow: 1, minHeight: 0, overflow: 'auto' }}>
            <Typography color='text.secondary'>{t_('This file format cannot be previewed')}</Typography>
            {/* RN25 + RN16: descargar solo si el grupo lo concede; si no, no está — nunca en gris. */}
            {permissions.download
              ? (
                <Button variant='contained' onClick={() => handleDownload(entry, source)} sx={{ minHeight: 48, minWidth: 48 }}>
                  {t_('Download file')}
                </Button>
                )
              : null}
          </Stack>
        </Stack>
      )
    }

    if (viewerErrorKey !== '') {
      return (
        <Stack spacing={2} sx={{ height: '100%', minHeight: 0 }}>
          {header}
          <Stack alignItems='center' justifyContent='center' sx={{ flexGrow: 1 }}>
            <Typography color='error'>{t_(viewerErrorKey)}</Typography>
          </Stack>
        </Stack>
      )
    }

    return (
      <Stack spacing={1} sx={{ height: '100%', minHeight: 0 }}>
        {header}
        {/* `minHeight: 0` en un hijo flex es lo que le deja al visor una altura REAL en la que
            pintar, y `overflow: 'auto'` le da SU PROPIO scroll: un plano grande o un PDF de varias
            hojas se recorre aquí dentro, sin arrastrar el modal entero ni la lista de la izquierda. */}
        <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          <FabricationDocumentViewer
            // Remontar en cada eleccion desde la lista deja el gesto sin ambiguedad: lo que se pulsa
            // es lo que se ve, aunque el carrusel estuviera en otro documento del mismo registro.
            key={openedDocument.pickToken}
            documents={documentsOfOpenedRecord}
            initialDocumentUUID={openedDocument.requestedUUID}
            contextQuery={buildMosDocumentContextQuery(contextOf(source))}
            onLoadError={handleViewerLoadError}
            onDocumentChange={handleViewerDocumentChange}
          />
        </Box>
      </Stack>
    )
  }

  const reloadList = useCallback(() => setRetryToken((token) => token + 1), [])

  const backToListFromAction = useCallback(() => {
    setAddOpen(false)
    setEditing(NO_ACTION)
    setDeleting(NO_ACTION)
  }, [])

  // S69/RN1/RN23: el alta recorre el MISMO camino documental que MIC (N3), con el contexto MOS
  // delante. Destino REAL de escritura y destino DECLARADO salen del mismo `source` (TAREA 0).
  const handleAddSubmit = useCallback(({ source, payload, file }) => {
    apiFile(buildMosDocumentCreateUrl(contextOf(source)), payload, file)
      .then(() => {
        backToListFromAction()
        // S75: la recarga la provoca la acción del PROPIO operario, no el sondeo de fondo.
        reloadList()
      })
      .catch((error) => {
        backToListFromAction()
        setListNoticeRaw(error?.message || '')
      })
  }, [contextOf, backToListFromAction, reloadList])

  const startEdit = useCallback((entry, source) => {
    setListNoticeKey('')
    setListNoticeRaw('')
    setEditValues({
      documentName: entry.document?.documentName || '',
      description: entry.document?.description || ''
    })
    setEditing({ entry, source })
  }, [])

  // S73/RN18: editar alcanza SOLO a los metadatos. El contenido del fichero no se toca desde aquí,
  // ni se anota, ni se firma.
  const handleEditSave = useCallback(() => {
    const { entry, source } = editing
    apiPut(buildMosDocumentUpdateUrl(contextOf(source)), {
      ...entry.document,
      documentName: editValues.documentName,
      description: editValues.description
    })
      .then(() => {
        backToListFromAction()
        reloadList()
      })
      .catch((error) => {
        backToListFromAction()
        setListNoticeRaw(error?.message || '')
      })
  }, [editing, editValues, contextOf, backToListFromAction, reloadList])

  const startDelete = useCallback((entry, source) => {
    setListNoticeKey('')
    setListNoticeRaw('')
    setDeleting({ entry, source })
  }, [])

  // S74: la petición lleva la PROCEDENCIA desde la que se pulsó, no solo el uuid del documento.
  const handleDeleteConfirm = useCallback(() => {
    const { entry, source } = deleting
    apiDelete(buildMosDocumentDeleteUrl(entry.document?.id, contextOf(source)))
      .then(() => {
        backToListFromAction()
        reloadList()
      })
      .catch((error) => {
        // RN34/CA31: el aviso de MIC se muestra TAL CUAL y el documento sigue en la lista, en todos
        // sus grupos. No se recarga: nada ha cambiado en el servidor.
        backToListFromAction()
        setListNoticeRaw(error?.message || '')
      })
  }, [deleting, contextOf, backToListFromAction, reloadList])

  const renderEdit = () => (
    <Stack spacing={2} sx={{ width: '100%', textAlign: 'left' }} data-testid='edit-document-form'>
      <Typography variant='subtitle1' sx={{ fontWeight: 800 }}>{t_('Edit')}</Typography>
      <TextField
        label={t_('Document name')}
        value={editValues.documentName}
        onChange={(event) => setEditValues((previous) => ({ ...previous, documentName: event.target.value }))}
        inputProps={{ 'aria-label': t_('Document name') }}
      />
      <TextField
        label={t_('Description')}
        value={editValues.description}
        onChange={(event) => setEditValues((previous) => ({ ...previous, description: event.target.value }))}
        inputProps={{ 'aria-label': t_('Description') }}
      />
      <Stack direction='row' spacing={1}>
        <Button variant='contained' onClick={handleEditSave} sx={{ minHeight: 48, minWidth: 48 }}>{t_('Save')}</Button>
        <Button variant='outlined' onClick={backToListFromAction} sx={{ minHeight: 48, minWidth: 48 }}>{t_('Cancel')}</Button>
      </Stack>
    </Stack>
  )

  const renderDeleteConfirmation = () => (
    <Stack spacing={2} sx={{ width: '100%', textAlign: 'left' }} data-testid='delete-confirmation'>
      <Typography variant='subtitle1' sx={{ fontWeight: 800 }}>
        {t_('Delete')} · {deleting.entry.document?.documentName || ''}
      </Typography>
      {/* La confirmación NOMBRA el registro sobre el que se va a actuar (S74). */}
      <Typography>
        {t_('Record')}: {deleting.source.sourceCode || ''}
      </Typography>
      <Typography color='text.secondary'>
        {t_('The document will be deleted from MIC permanently if it only hangs from this record.')}
      </Typography>
      <Typography color='text.secondary'>
        {t_('If it is linked to another record the server will reject it and nothing will change.')}
      </Typography>
      {/* D19/S98 — en features y grupos de feature el efecto no se queda en esta fabricación. Los
          siete tipos originales conservan exactamente la confirmación de S74, sin aviso nuevo. */}
      {SHARED_QUALITY_CATALOGUE_TYPES.includes(deleting.source.sourceType)
        ? (
          <Typography color='warning.main'>
            {t_('This document belongs to the shared quality catalogue: it hangs from the feature or feature group itself, not from the task it was pressed from, and will be visible from every task, fabrication, part or process that uses it.')}
          </Typography>
          )
        : null}
      <Stack direction='row' spacing={1}>
        <Button variant='contained' color='error' onClick={handleDeleteConfirm} sx={{ minHeight: 48, minWidth: 48 }}>
          {t_('Confirm')}
        </Button>
        <Button variant='outlined' onClick={backToListFromAction} sx={{ minHeight: 48, minWidth: 48 }}>
          {t_('Cancel')}
        </Button>
      </Stack>
    </Stack>
  )

  const renderBody = () => {
    if (editing.entry !== '') {
      return renderEdit()
    }

    if (deleting.entry !== '') {
      return renderDeleteConfirmation()
    }

    if (loading) {
      return (
        <Stack alignItems='center' justifyContent='center' sx={{ minHeight: 200 }}>
          <CircularProgress />
        </Stack>
      )
    }

    if (hasFailed) {
      return (
        <Stack alignItems='center' justifyContent='center' spacing={2} sx={{ minHeight: 200 }}>
          <Typography color='error'>{t_('The documentation could not be loaded')}</Typography>
          {errorLiteral !== '' ? <Typography color='text.secondary'>{t_(errorLiteral)}</Typography> : null}
          <Button variant='outlined' onClick={handleRetry} sx={{ minHeight: 48, minWidth: 48 }}>
            {t_('Retry')}
          </Button>
        </Stack>
      )
    }

    // RN14 matizada por la enmienda (RN41.2/S91): «sin documentación» ya NO es «`groups` está
    // vacío», es «ningún grupo aporta ni una entrada». Un grupo puede viajar solo por ser destino de
    // alta, con `entries` vacío: entonces el aviso se muestra IGUALMENTE, y esos grupos van DEBAJO
    // (RN41.7) para que el operario pueda colgar el primer documento sin salir del modal.
    const contributesDocuments = documentation.groups.some((group) => group.entries.length > 0)

    if (documentation.groups.length === 0) {
      return (
        <Stack alignItems='center' justifyContent='center' sx={{ minHeight: 200 }}>
          <Typography color='text.secondary'>{t_('This fabrication has no documentation available')}</Typography>
        </Stack>
      )
    }

    const visibleGroups = filterGroupsByDocumentName(documentation.groups, searchText)
    const selectedKey = openedDocument.entry === ''
      ? ''
      : openedDocument.source.sourceType + '-' + openedDocument.source.sourceReferenceId + '-' +
        openedDocument.entry.document?.uuid

    const leftPane = (
      <Stack spacing={2} sx={{ width: '100%', textAlign: 'left' }}>
        {listNoticeKey !== ''
          ? <Typography color='warning.main'>{t_(listNoticeKey)}</Typography>
          : null}
        {/* Texto de MIC, verbatim: no pasa por `t_` (RN34/S74). */}
        {listNoticeRaw !== ''
          ? <Typography color='warning.main'>{listNoticeRaw}</Typography>
          : null}

        {/* RN14/RN41.7/S91 — el aviso va ARRIBA y los grupos de destino DEBAJO, en ese orden. */}
        {!contributesDocuments
          ? <Typography color='text.secondary'>{t_('This fabrication has no documentation available')}</Typography>
          : null}

        {/* RN21/RN41.3/S70/S99: la acción de añadir solo existe si ALGÚN grupo trae `add` Y algún
            destino. Si no, no está —ni en gris ni con explicación— y el modal tampoco acepta soltar
            ficheros encima. */}
        {documentation.groups.some((group) => group.permissions?.add && (group.targets?.length || 0) > 0)
          ? (
            <Box>
              <Button variant='outlined' onClick={() => setAddOpen(true)} sx={{ minHeight: 48, minWidth: 48 }}>
                {t_('Add document')}
              </Button>
            </Box>
            )
          : null}
        <TextField
          size='small'
          fullWidth
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder={t_('Search by document name')}
          inputProps={{ 'aria-label': t_('Search by document name') }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />

        {/* E9/S65: «sin resultados» es un mensaje propio, DISTINTO del de «esta fabricación no tiene
            documentación», y no es un error de sistema. */}
        {visibleGroups.length === 0 && contributesDocuments
          ? (
            <Stack alignItems='center' justifyContent='center' sx={{ minHeight: 160 }}>
              <Typography color='text.secondary'>{t_('No documents match the search')}</Typography>
            </Stack>
            )
          : null}

        <FabricationDocumentGroupList
          groups={visibleGroups}
          selectedKey={selectedKey}
          // Mientras se busca, el plegado del operario se ignora: un resultado escondido tras una
          // cabecera cerrada es un resultado que no encuentra (y concluiria que no existe).
          forceExpanded={searchText.trim() !== ''}
          onView={openDocument}
          onDownload={handleDownload}
          onEdit={startEdit}
          onDelete={startDelete}
        />
      </Stack>
    )

    return (
      <Box
        data-testid='documents-panes'
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          width: '100%',
          // El papel mide 85vh; la cabecera del diálogo y su barra de acciones se llevan ~150px.
          // Fijar aquí la altura es lo que garantiza que CADA panel desborde y saque SU barra, sin
          // depender de que `flexGrow` sobreviva a `DialogContent` y a los dos `Grid` intermedios.
          height,
          maxHeight: height,
          minHeight: 0,
          overflow: 'hidden'
        }}
      >
        <Box
          data-testid='documents-list-pane'
          sx={{
            // Proporcion, no ancho fijo: en la pantalla del puesto la lista necesita sitio para que
            // el nombre del documento se lea de un vistazo, y el visor sigue llevandose la mayoria.
            width: { xs: '100%', md: '42%' },
            minWidth: { md: 420 },
            maxWidth: { md: 700 },
            flexShrink: 0,
            height: { xs: 'auto', md: '100%' },
            minHeight: 0,
            overflowY: 'auto',
            pr: { md: 1 }
          }}
        >
          {leftPane}
        </Box>

        <Divider orientation='vertical' flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

        <Box
          data-testid='documents-viewer-pane'
          sx={{
            flexGrow: 1,
            minWidth: 0,
            minHeight: 0,
            height: { xs: 360, md: '100%' },
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          {renderViewerPane()}
        </Box>
      </Box>
    )
  }

  return (
    <>
      {renderBody()}

      {/* ⚠️ EL ALTA SE SUPERPONE, NO SUSTITUYE (rediseño del PO, 2026-09-21). Antes ocupaba el modal
          entero y el operario perdía de vista la documentación que ya hay, que es justo la referencia
          de dónde va lo que está subiendo. Ahora es un segundo diálogo, acotado, con la lista de
          siempre visible detrás. */}
      <MosSmartDialog
        setOpen={addOpen}
        accept={false}
        cancel={false}
        close
        closeCallback={backToListFromAction}
        title={createDialogTitle({ color: 'primary', title: t_('Add document'), icon: null })}
        minWidth='min(760px, 90vw)'
        paperWidth='760px'
        maxPaperWidth='90vw'
        maxPaperHeight='80vh'
      >
        {addOpen
          ? (
            <FabricationDocumentAddForm
              groups={documentation.groups}
              onSubmit={handleAddSubmit}
              onCancel={backToListFromAction}
            />
            )
          : ''}
      </MosSmartDialog>
    </>
  )
}

FabricationDocumentsPanel.propTypes = {
  fabricationOrderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  jobReferenceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  requestToken: PropTypes.number,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

export default FabricationDocumentsPanel
